import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  requestGetPointBalance,
  requestGetRewardsProduct,
  usePointDispatch,
  usePointState,
} from "../../Context";
import Markdown from "react-markdown";
import { rawMarkdownRehypePlugins } from "../../Utils/markdownSanitize";
import remarkGfm from "remark-gfm";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { Button } from "../../Components/Button";
import { Alert } from "../../Components/Alert";
import ProductDetailSkeleton from "../../Components/ProductDetailSkeleton";

const RewardProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const pointDispatch = usePointDispatch();

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const {
    balance,
    loading,
    error,
    product,
    productDetailLoading,
    productDetailError,
  } = usePointState();

  const fetchProductDetails = useCallback(() => {
    if (id) {
      requestGetPointBalance(pointDispatch);
      requestGetRewardsProduct(pointDispatch, { id });
    }
  }, [id, pointDispatch]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  useEffect(() => {
    const image = new Image();
    image.src = product?.imageUrl;

    image.onload = () => {
      setIsImageLoaded(true);
    };

    return () => {
      image.onload = null;
    };
  }, [product?.imageUrl]);

  const getButtonTitle = () => {
    if (product.availableStock === 0) return "outOfStock";
    if (balance?.balance < product?.point && product?.availableStock > 0)
      return "insufficientPoints";
    return "redeemPoints";
  };

  if (error || productDetailError) {
    return (
      <div className="bg-white min-h-full rounded-lg p-4">
        <Alert
          type="error"
          title="Error"
          message={error?.message || productDetailError?.message}
          errors={error?.errors || productDetailError?.errors}
        />
      </div>
    );
  }

  return productDetailLoading ? (
    <ProductDetailSkeleton />
  ) : (
    <div className="max-w-4xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
      <div className="lg:col-start-3 lg:col-span-1">
        <div className="p-4 flex flex-col bg-white rounded-lg text-sm divide-y">
          <div className="mb-2">
            <span className="text-gray-900 font-medium">{product?.name}</span>
            {product?.availableStock ? (
              product.availableStock < 10 ? (
                <div className="flex space-x-1 text-amber-500 font-medium items-center">
                  <InformationCircleIcon className="h-4 w-4" />
                  <span>{t("remaining")}</span>
                  <span>{product.availableStock}</span>
                </div>
              ) : (
                <div className="flex space-x-1 text-green-500 font-medium items-center">
                  <span>{t("available")}</span>
                </div>
              )
            ) : null}
          </div>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col-reverse mt-2">
              <span className="font-medium text-blue-500">
                {product.point?.toLocaleString("id-ID")} {t("point")}
              </span>
              {product.basePoint && product.basePoint > product.point ? (
                <span className="text-xs line-through text-gray-400">
                  {product.basePoint?.toLocaleString("id-ID")} {t("point")}
                </span>
              ) : null}
            </div>
            <Button
              loading={loading}
              disabled={
                loading ||
                (balance?.balance < product?.point &&
                  product?.availableStock > 0) ||
                product.availableStock === 0
              }
              title={getButtonTitle()}
              onClick={() => navigate(`/reward-redemption/${product.id}`)}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col space-y-6 lg:col-start-1 lg:col-span-2">
        {product?.imageUrl ? (
          <div>
            {isImageLoaded ? (
              <div>
                <img
                  src={product.imageUrl}
                  alt="Banner"
                  className="block sm:block md:block lg:hidden rounded-md"
                />
                <div className="hidden sm:hidden md:hidden lg:block relative overflow-hidden w-full h-96 rounded-lg">
                  <div
                    className="hidden sm:block absolute top-0 left-0 w-full h-full bg-cover blur-[50px] brightness-90 mask-gradient rounded-lg"
                    style={{
                      backgroundImage: `url('${product.imageUrl}')`,
                    }}
                  ></div>
                  <img
                    src={product.imageUrl}
                    alt="Banner"
                    className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-full max-h-full z-10"
                  />
                </div>
              </div>
            ) : (
              <div className="relative overflow-hidden w-full h-96 rounded-lg">
                <div className="absolute top-0 left-0 w-full h-full bg-gray-300 animate-pulse"></div>
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 max-w-full max-h-full z-10">
                  <div className="w-4/5 h-4/5 bg-white mx-auto my-4 animate-pulse"></div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-96 text-center rounded-lg overflow-hidden bg-gray-200 text-base sm:text-lg  text-gray-500 flex flex-col justify-center">
            {t("No Image")}
          </div>
        )}
        <div className="flex flex-col divide-y bg-white rounded-lg">
          <div className="px-4 py-3 text-sm font-medium text-gray-900">
            {t("Description")}
          </div>
          <div className="px-4 py-3 text-sm text-gray-500 text-justify">
            <Markdown
              rehypePlugins={rawMarkdownRehypePlugins}
              remarkPlugins={[remarkGfm]}
              components={{
                p({ children }) {
                  return <p className="mb-2 last:mb-0">{children}</p>;
                },
                ol({ children }) {
                  return (
                    <ol className="list-inside list-decimal">{children}</ol>
                  );
                },
                ul({ children }) {
                  return <ul className="list-inside list-disc">{children}</ul>;
                },
                li({ children }) {
                  return (
                    <li className="mb-2 list-item list-inside">{children}</li>
                  );
                },
                blockquote({ children }) {
                  return (
                    <blockquote className="relative border-s-4 border-gray-800 bg-slate-200 pl-2 ps-4 sm:ps-6">
                      {children}
                    </blockquote>
                  );
                },
              }}
            >
              {product?.description}
            </Markdown>
          </div>
        </div>
        <div className="flex flex-col divide-y bg-white rounded-lg">
          <div className="px-4 py-3 text-sm font-medium text-gray-900">
            {t("termsTitle")}
          </div>
          <div className="px-4 py-3 text-sm text-gray-500 text-justify">
            <Markdown
              rehypePlugins={rawMarkdownRehypePlugins}
              remarkPlugins={[remarkGfm]}
              components={{
                ol({ children }) {
                  return (
                    <ol className="list-inside list-decimal">{children}</ol>
                  );
                },
                ul({ children }) {
                  return <ul className="list-inside list-disc">{children}</ul>;
                },
                li({ children }) {
                  return (
                    <li className="mb-2 list-item list-inside">{children}</li>
                  );
                },
                blockquote({ children }) {
                  return (
                    <blockquote className="relative border-s-4 border-gray-800 bg-slate-200 pl-2 ps-4 sm:ps-6">
                      {children}
                    </blockquote>
                  );
                },
              }}
            >
              {product?.terms}
            </Markdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardProductDetail;
